import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  AdminMatchmakingQueueEntry,
  AdminMatchmakingQueueResponse,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import PageHeader from '../../components/PageHeader';
import Pill from '../../components/Pill';
import { MATCHMAKING } from '@shared/constants';
import { getMatchmakingQueue } from '../../services/liveActivity';
import { ApiError } from '../../services/api';

/**
 * Live matchmaking queue — the drill-down behind the dashboard's
 * "In matchmaking" card.
 *
 * Auto-refreshes: the queue is a Redis structure that turns over in seconds
 * (bot fallback fires at 8s, timeout at 30s), so a static snapshot is stale
 * before it finishes rendering. The poll is paused while the tab is hidden —
 * an admin panel left open on a second monitor should not hold a request
 * loop open all afternoon.
 */

const POLL_MS = 5000;
const BOT_FALLBACK_MS = MATCHMAKING.BOT_FALLBACK_AFTER_SECONDS * 1000;

export default function MatchmakingQueuePage() {
  const navigate = useNavigate();
  // Last-good data is kept ALONGSIDE the error rather than replaced by it: a
  // single transient failure on the 5s poll would otherwise wipe a table that
  // was correct a moment ago and drop the header to "0 waiting".
  const [data, setData] = useState<AdminMatchmakingQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reqCounterRef = useRef(0);
  const fetchQueue = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    try {
      const next = await getMatchmakingQueue();
      if (reqId !== reqCounterRef.current) return;
      setData(next);
      setError(null);
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load the matchmaking queue';
      setError(message);
    } finally {
      if (reqId === reqCounterRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQueue();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void fetchQueue();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchQueue]);

  const rows = data?.entries ?? [];
  const total = data?.total ?? 0;

  const columns: DataTableColumn<AdminMatchmakingQueueEntry>[] = useMemo(
    () => [
      {
        key: 'player',
        header: 'Player',
        render: (e) => (
          <span className="font-medium text-slate-900">
            {e.displayName}
            <span className="ml-1 text-xs font-normal text-slate-500">
              @{e.username}
            </span>
          </span>
        ),
      },
      {
        key: 'kind',
        header: 'Kind',
        render: (e) =>
          e.isBot ? (
            <Pill variant="accent">Bot</Pill>
          ) : (
            <Pill variant="neutral">Player</Pill>
          ),
      },
      {
        key: 'language',
        header: 'Lang',
        render: (e) => (
          <span className="font-mono text-xs uppercase text-slate-700">
            {e.language}
          </span>
        ),
      },
      {
        key: 'mode',
        header: 'Mode',
        render: (e) => <span className="text-sm text-slate-700">{e.mode}</span>,
      },
      {
        key: 'totalWins',
        header: 'Wins',
        render: (e) => (
          <span className="font-mono text-sm text-slate-700">
            {e.totalWins}
          </span>
        ),
      },
      {
        key: 'waiting',
        header: 'Waiting',
        render: (e) => (
          // Amber past the bot-fallback mark: a player still queueing after
          // that is waiting longer than the design intends. Sourced from the
          // shared constant so retuning the fallback cannot leave the admin
          // panel colouring against a stale threshold.
          <span
            className={`font-mono text-sm ${
              e.waitingMs >= BOT_FALLBACK_MS ? 'text-amber-700' : 'text-slate-700'
            }`}
          >
            {formatWait(e.waitingMs)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="matchmaking-queue-screen">
      <PageHeader
        title="Matchmaking queue"
        subtitle={`${total} waiting · refreshes every ${POLL_MS / 1000}s`}
        testId="matchmaking-queue-header"
      />

      {error !== null && (
        <div
          role="alert"
          data-testid="matchmaking-queue-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {error}
          {data !== null && ' — showing the last successful refresh.'}
        </div>
      )}

      {data !== null && total > rows.length && (
        <div
          data-testid="matchmaking-queue-orphans"
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
        >
          {total - rows.length} queue{' '}
          {total - rows.length === 1 ? 'entry is' : 'entries are'} not shown —
          either the user row no longer exists or the entry could not be
          parsed. They still count toward the dashboard figure and are cleaned
          up when the queue expires them.
        </div>
      )}

      <DataTable<AdminMatchmakingQueueEntry>
        columns={columns}
        rows={rows}
        rowKey={(e) => e.userId}
        loading={loading}
        onRowClick={(e) => navigate(`/users/${e.userId}`)}
        empty="Nobody is waiting for a match right now."
        testId="matchmaking-queue-table"
      />
    </div>
  );
}

function formatWait(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}
